import { products } from 'projects/gung-standard/src/lib/models/dummydata';
import { Injectable } from '@angular/core';
import {
  AuthService,
  ProductService,
  ProductArticleClassFilterService,
  ProductSortService,
  MetadataService,
  Product,
  Assortment,
  AssortmentService,
  GungFlowService,
  LocationConfigService,
  ProductConcept,
  CloudPimFiltersService,
  CurrentAssortmentService
} from 'gung-standard';
import {
  JeevesProductGridViewComponent,
  JeevesProductListConfigService,
  JeevesProductTableComponent,
  ProductItemGroupFilterService
} from 'gung-standard-jeeves';
import { ConfigBaseFilter, ListLayout, ListLayoutMultipleComponent, SimpleConfigBaseFilter } from 'gung-list';
import { TranslateService } from '@ngx-translate/core';
import { map, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs';
import { TyloProductGridViewComponent } from '../components/tylo-product-grid-view/tylo-product-grid-view.component';
import { TyloProductTableComponent } from '../components/tylo-product-table/tylo-product-table.component';
import { environment } from '../../environments/environment';

export interface AssortmentRecursive extends Assortment {
  children: (Assortment | AssortmentRecursive)[];
  parent?: string[];
  extra?: { [s: string]: any } | null;
}
export class ItemTypeFilterI18n extends SimpleConfigBaseFilter<Product> {
  constructor(
    protected filterTitle: string,
    protected field: string,
    protected language: string,
    protected backupPimField
  ) {
    super();
  }

  getName(): string {
    return this.filterTitle;
  }

  getOptionIds(item: any /* Assortment */): string[] {
    if (
      !item.extra.i18n ||
      !this.field ||
      !item.extra.i18n.hasOwnProperty(this.language) ||
      !item.extra.i18n[this.language][this.field]
    ) {
      // Small hack to fix filters for tylo-inc. They have the filter only in pim, not translateable which broke filtes.
      // To avoid having to make environment variables that will spread out in the code like hl-display, this would
      // fix this specific issue.
      if (item?.extra?.pim && item?.extra?.pim[this.backupPimField]) {
        return ['' + item.extra.pim[this.backupPimField]];
      } else {
        return ['UNDEFINED'];
      }
    }

    return ['' + item.extra.i18n[this.language][this.field]];
  }

  getOptionName(key: string): string {
    if (!key) {
      return '';
    }
    return key;
  }
}

@Injectable({
  providedIn: 'root'
})
export class TyloProductListConfigService extends JeevesProductListConfigService {
  topFilter = true;

  constructor(
    protected authService: AuthService,
    protected productService: ProductService,
    protected productItemGroupFilterService: ProductItemGroupFilterService,
    protected productArticleClassFilterService: ProductArticleClassFilterService,
    protected metadataService: MetadataService,
    protected productSortService: ProductSortService,
    protected languageService: TranslateService,
    protected gungFlowService: GungFlowService,
    protected locationConfigService: LocationConfigService,
    protected cloudPimFiltersService: CloudPimFiltersService,
    protected currentAssortmentService: CurrentAssortmentService
  ) {
    super(
      authService,
      productService,
      productItemGroupFilterService,
      productArticleClassFilterService,
      productSortService,
      gungFlowService,
      locationConfigService,
      cloudPimFiltersService,
      currentAssortmentService
    );
  }

  getLayouts(): ListLayout<Product>[] {
    return [
      {
        getIconClass: () => 'fa fa-th',
        getListItemComponent: () => TyloProductGridViewComponent,
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'ProductGrid'
      },
      {
        getIconClass: () => 'fa fa-list',
        getListItemComponent: () => TyloProductTableComponent,
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'ProductList'
      }
    ];
  }

  getFilters(): ConfigBaseFilter<Product>[] {
    const filters = [
      // this.productArticleClassFilterService,
      // this.productItemGroupFilterService
    ];

    const lang = this.languageService.currentLang || 'se';

    if (environment.projectName == 'helo') {
      return filters;
    }

    filters.push(new ItemTypeFilterI18n('MAIN_CATEGORY', 'mainCatagory', lang, 'mainCategory')); // NOTE: mainCatagory <> mainCategory. tylo-inc however uses mainCategory (it's a mess)
    filters.push(new ItemTypeFilterI18n('SUB_CATEGORY_1', 'subCatagory', lang, 'subCategory')); // NOTE: subCatagory <> subCategory . tylo-inc however uses mainCategory (it's a mess)
    filters.push(new ItemTypeFilterI18n('CATEGORY_LEVEL_3', 'categoryLevel3', lang, 'categoryLevel3'));

    return filters;
  }

  getAddQueryParameterLimitToUrl(): boolean {
    return true;
  }

  getItems(): Observable<Product[]> {
    return this.assortmentSubject.pipe(
      switchMap((assortment: string) => {
        if (assortment == null) {
          return this.authService.getCurrentUser().pipe(switchMap(user => this.fetchProducts(user.assortment)));
        }
        return this.fetchProducts(assortment).pipe(
          switchMap((products: Product[]) => {
            const sortedProducts = products.sort((a, b) => {
              const aSort = a.extra.pim && a.extra.pim.sequence;
              const bSort = b.extra.pim && b.extra.pim.sequence;

              if ((!bSort && aSort) || (aSort && bSort && Number(aSort) > Number(bSort))) {
                return -1;
              } else if ((!aSort && bSort) || (aSort && bSort && Number(aSort) < Number(bSort))) {
                return 1;
              } else {
                return 0;
              }
            });
            return of(sortedProducts);
          })
        );
      })
    );
  }

  getSearchTerms(item: Product) {
    let terms = super.getSearchTerms(item);

    if (!!item.extra?.ar?.q_ar_sstl) {
      terms.push(item.extra.ar.q_ar_sstl);
    }

    if (item.extra.i18n?.en?.name) {
      terms.push(item.extra.i18n.en.name);
    }
    if (item.extra.i18n?.se?.name) {
      terms.push(item.extra.i18n.se.name);
    }
    if (item.extra.ar?.artnr) {
      terms.push(item.extra.ar.artnr);
    }
    

    if((item as ProductConcept).subProductIds ){
      terms = [...terms, ...(item as ProductConcept).subProductIds]
    }

    return terms;
  }
}
